// FIRST: must run before any module builds a Zod schema (see the file).
import './lib/zod-jitless.js';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import type { ClientConfig } from '@ferro/config';
import { App } from './App.js';
import { loadTenantBrand } from './lib/loadTenantBrand.js';
import { pickTenantConfig } from './lib/pickTenantConfig.js';
import { initSentryIfConfigured } from './lib/sentry.js';
// @ferro/ui primitives (Modal, Card, Alert, PageHeader, …) ship their
// styles in a separate sheet the app must import once — customer-app
// has always done this; admin-app never did, so every @ferro/ui
// component rendered unstyled (#221 Wave 1). Import it BEFORE
// index.css so admin-local rules keep cascade precedence on any
// shared selectors (e.g. .ferro-badge, .ferro-btn).
import '@ferro/ui/styles.css';
import './index.css';

// Fire-and-forget, FIRST: no-op without VITE_SENTRY_DSN; otherwise it
// installs a pre-init error buffer synchronously (so even the #root throw
// below and other boot-time crashes are captured) and lazy-loads the
// @sentry/react chunk off the critical path (see lib/sentry.ts).
initSentryIfConfigured();

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('#root element not found');
}

const root = ReactDOM.createRoot(rootEl);

function renderApp(config: ClientConfig): void {
  root.render(
    <React.StrictMode>
      <BrowserRouter>
        <App config={config} />
      </BrowserRouter>
    </React.StrictMode>,
  );
}

// Stage 2 — Runtime tenant resolution. `pickTenantConfig()` calls the
// platform's `/api/v2/tenant/by-host` echo (set up by Stage 1.5 host
// middleware). For `<slug>.admin.animalloversapp.com` it resolves to
// that tenant's config; for bare `admin.animalloversapp.com` (the
// platform-operator console per ADR-2026-04-28), the lookup returns
// null and we fall through to the ALA static config. Auth-level
// gating for platform_admin role is handled by PlatformAdminRoute.
//
// w0.2 / #247 — `loadTenantBrand` overlays the DB-backed `TenantBranding`
// row on top of the static baseline, but it is OFF the blocking path:
// first paint waits only for pickTenantConfig (≤ ~2.25s worst case —
// the #247 review's first-paint budget), and the brand overlay
// re-renders when it lands (`installBrandStyles` runs in an effect
// keyed on `config`, so the overlay applies on the second render). The
// static tenant brand is already the CORRECT tenant; the DB overlay is
// incremental polish, never worth a blank screen. Both functions never
// throw.
pickTenantConfig().then((baseConfig) => {
  renderApp(baseConfig);
  void loadTenantBrand(baseConfig).then((config) => {
    if (config !== baseConfig) renderApp(config);
  });
});
